import { Portal } from "@radix-ui/react-portal";
import { useQuery } from "@tanstack/react-query";
import { capitalCase } from "change-case";
import { XIcon } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { RemoveScroll } from "react-remove-scroll";
import { CartesianGrid, Line, LineChart, XAxis, YAxis } from "recharts";

import { Spinner } from "@/components/Spinner";
import { Button } from "@/components/ui/button";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart";
import { analyticsChartConfig } from "@/components/ui/chart-constants";
import { SimpleSelect } from "@/components/ui/select";
import {
  CampaignAnalytic,
  FeedAnalytics,
  StatsSummary,
  userStatsQueryOptions,
} from "@/lib/api";
import { cn, formatBigNumber, formatDate } from "@/lib/utils";

export function UserAnalytics({
  analytics,
  title,
  dataKey,
  variant = "default",
  bigValue,
  onEnterFullscreen,
}: {
  analytics: FeedAnalytics[] | CampaignAnalytic[];
  title: string;
  dataKey:
    | Exclude<keyof FeedAnalytics, "date">
    | Exclude<keyof CampaignAnalytic, "view_date">;
  variant?: "default" | "compact";
  bigValue: number;
  onEnterFullscreen?: () => void;
}) {
  return (
    <button
      onClick={onEnterFullscreen}
      type="button"
      className={cn(
        "flex-1 min-w-0",
        "cursor-pointer-extra text-left",
        "relative group",
        "border rounded-md border-slate-6 bg-slate-subtle dark:border-slatedark-6",
        "flex md:gap-3",
        variant === "default"
          ? "flex-1 gap-4 sm:gap-2 items-center sm:items-stretch sm:flex-col p-4 min-w-[160px] sm:min-w-[160px] max-h-[160px] lg:max-h-[200px]"
          : "md:flex-1 gap-2 px-3 py-2 md:p-4 min-w-[120px] md:min-w-[160px]",
      )}
    >
      <div
        className={cn(
          "flex flex-col gap-1 sm:gap-2",
          variant === "default" ? "w-1/4 md:w-auto" : "",
        )}
      >
        <span
          className={cn(
            "font-semibold text-slate-dim text-xs",
            variant === "default" ? "text-xs sm:text-base" : "",
          )}
        >
          {title}
        </span>{" "}
        <div
          className={cn(
            "font-black",
            variant === "default" ? "sm:text-3xl" : "",
          )}
        >
          {formatBigNumber(bigValue)}
        </div>
      </div>
      <ChartContainer
        config={analyticsChartConfig}
        className={cn(
          "w-full flex-1 min-h-0 min-w-0",
          variant === "default"
            ? "h-[50px] sm:h-auto"
            : "h-[50px] hidden md:block",
        )}
      >
        <LineChart data={analytics}>
          <ChartTooltip
            content={
              <ChartTooltipContent
                formatter={(value, _, item) => {
                  return (
                    <div className="text-slate-dim flex gap-2 justify-between w-full">
                      <div className="text-xs">
                        {typeof item === "object"
                          ? formatDate(
                              (
                                item as unknown as {
                                  payload: { view_date: string };
                                }
                              ).payload.view_date,
                            )
                          : null}
                      </div>
                      <div>
                        {Intl.NumberFormat("en-us").format(value as number)}
                      </div>
                    </div>
                  );
                }}
              />
            }
          />
          <Line
            type="monotone"
            className="text-slate-11 dark:text-slatedark-11"
            dataKey={dataKey}
            strokeWidth={4}
            stroke="currentColor"
            dot={variant === "compact" ? false : true}
          />
        </LineChart>
      </ChartContainer>
    </button>
  );
}

type AnalyticsQuery = (options: {
  days: number;
}) => ReturnType<typeof userStatsQueryOptions>;

const analyticsKeys = [
  "unique_user_count",
  "post_count",
  "post_view_count",
  "feed_view_count",
] as const;

function FullScreenAnalytics({
  query,
  onExitFullscreen,
  initialKey,
}: {
  query: AnalyticsQuery;
  onExitFullscreen: () => void;
  initialKey: keyof FeedAnalytics;
}) {
  const [days, setDays] = useState(30);
  const { data: analytics, isPending } = useQuery(query({ days }));
  const [dataKey, setDataKey] =
    useState<Exclude<keyof FeedAnalytics, "date">>(initialKey);

  return (
    <RemoveScroll>
      <div className="fixed inset-0 bg-slate-app z-30 flex flex-col">
        <div className="flex items-center gap-2 p-4 border-b border-slate-6 dark:border-slatedark-6">
          <div className="pr-4 text-slate-normal font-semibold text-xl flex items-center">
            Analytics
          </div>

          <div className="hidden md:flex items-center gap-2">
            {analyticsKeys.map((key) => (
              <Button
                key={key}
                size="sm"
                variant={dataKey === key ? "action" : "ghost"}
                onClick={() => setDataKey(key)}
              >
                {capitalCase(key.replace("_count", "s"))}
              </Button>
            ))}
          </div>
          <div className="flex-1" />
          <div className="flex items-center justify-end gap-2">
            <SimpleSelect
              className="md:hidden"
              aria-label="Select data key"
              options={analyticsKeys.map((key) => ({
                label: capitalCase(key.replace("_count", "s")),
                value: key,
              }))}
              value={dataKey}
              onValueChange={(value) =>
                setDataKey(value as keyof FeedAnalytics)
              }
            />
            <SimpleSelect
              aria-label="Time period"
              options={["7", "30", "60", "90", "180", "360"].map((key) => ({
                label: `${key} days`,
                value: key,
              }))}
              value={`${days}`}
              onValueChange={(value) => setDays(parseInt(value))}
            />
          </div>
          <Button
            size="smIcon"
            className="text-slate-normal"
            variant="ghost"
            onClick={onExitFullscreen}
            aria-label="Exit fullscreen"
          >
            <XIcon />
          </Button>
        </div>

        {analytics?.details ? (
          <ChartContainer
            config={analyticsChartConfig}
            className={cn("w-full flex-1 min-h-0 px-4 py-6")}
          >
            <LineChart data={analytics.details}>
              <CartesianGrid strokeDasharray="3 3" />
              <YAxis
                tick={{ transform: "translate(-8, 0)" }}
                axisLine={false}
                tickLine={false}
              />
              <XAxis
                dataKey="view_date"
                className="p-10"
                tickFormatter={formatDate}
                tick={{ transform: "translate(0, 12)" }}
                axisLine={false}
                tickLine={false}
              />
              <ChartTooltip
                content={
                  <ChartTooltipContent
                    formatter={(value, _, item) => {
                      return (
                        <div className="text-slate-dim flex gap-2 justify-between w-full">
                          <div className="text-xs text-slate-dim">
                            {typeof item === "object"
                              ? formatDate(
                                  (
                                    item as unknown as {
                                      payload: { view_date: string };
                                    }
                                  ).payload.view_date,
                                )
                              : null}
                          </div>
                          <div>
                            {Intl.NumberFormat("en-us").format(value as number)}
                          </div>
                        </div>
                      );
                    }}
                  />
                }
              />
              <Line
                type="monotone"
                className="text-slate-11 dark:text-slatedark-11"
                dataKey={dataKey}
                strokeWidth={4}
                stroke="currentColor"
                dot
              />
            </LineChart>
          </ChartContainer>
        ) : isPending ? (
          <div className="flex-1 flex items-center justify-center">
            <Spinner />
          </div>
        ) : (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-slate-dim">No data</div>
          </div>
        )}
      </div>
    </RemoveScroll>
  );
}

export function UserAnalyticsSummary({
  analytics,
  variant = "default",
  summary,
  query,
}: {
  analytics: FeedAnalytics[];
  summary: Partial<StatsSummary>;
  variant?: "default" | "compact";
  query?: AnalyticsQuery;
}) {
  const [fullscreen, setFullscreen] = useState<keyof FeedAnalytics | null>(
    null,
  );
  const onEnterFullscreen = useCallback((key: keyof FeedAnalytics) => {
    setFullscreen(key);
  }, []);
  const onExitFullscreen = useCallback(() => {
    setFullscreen(null);
  }, []);

  useEffect(() => {
    if (fullscreen) {
      document.body.classList.add("fullscreen-editor");
    } else {
      document.body.classList.remove("fullscreen-editor");
    }
    return () => {
      document.body.classList.remove("fullscreen-editor");
    };
  }, [fullscreen]);

  return (
    <div
      className={cn(
        "flex-1 min-w-0",
        "flex gap-2 md:gap-4",
        variant === "compact"
          ? "flex-wrap"
          : "flex-wrap xl:flex-nowrap sm:flex-row",
      )}
    >
      {typeof summary.avg_unique_user_count !== "undefined" && (
        <UserAnalytics
          analytics={analytics}
          title="DAUs"
          dataKey="unique_user_count"
          variant={variant}
          bigValue={summary.avg_unique_user_count}
          onEnterFullscreen={
            query ? () => onEnterFullscreen("unique_user_count") : undefined
          }
        />
      )}
      {typeof summary.avg_post_count !== "undefined" && (
        <UserAnalytics
          analytics={analytics}
          title="Posts"
          dataKey="post_count"
          variant={variant}
          bigValue={summary.avg_post_count}
          onEnterFullscreen={
            query ? () => onEnterFullscreen("post_count") : undefined
          }
        />
      )}
      {typeof summary.avg_feed_like_count !== "undefined" && (
        <UserAnalytics
          analytics={analytics}
          title="Likes/day"
          dataKey="feed_like_count"
          variant={variant}
          bigValue={summary.avg_feed_like_count}
          onEnterFullscreen={
            query ? () => onEnterFullscreen("feed_like_count") : undefined
          }
        />
      )}
      {typeof summary.avg_post_view_count !== "undefined" && (
        <UserAnalytics
          analytics={analytics}
          title="Post Renders"
          dataKey="post_view_count"
          variant={variant}
          bigValue={summary.avg_post_view_count}
          onEnterFullscreen={
            query ? () => onEnterFullscreen("post_view_count") : undefined
          }
        />
      )}
      {typeof summary.avg_feed_view_count !== "undefined" && (
        <UserAnalytics
          analytics={analytics}
          title="Feed Requests"
          dataKey="feed_view_count"
          variant={variant}
          bigValue={summary.avg_feed_view_count}
          onEnterFullscreen={
            query ? () => onEnterFullscreen("feed_view_count") : undefined
          }
        />
      )}

      {fullscreen && query && (
        <Portal>
          <FullScreenAnalytics
            query={query}
            onExitFullscreen={onExitFullscreen}
            initialKey={fullscreen}
          />
        </Portal>
      )}
    </div>
  );
}
